/**
 * The consumer's own cars — screens B1 (list), B2 (form) and B3 (delete).
 *
 * Two server-side rules the UI has to respect rather than reimplement:
 *
 * - **Duplicate plates answer 409 `duplicate_plate`**, decided by a partial unique index
 *   on the normalized plate, with no SELECT-then-INSERT pre-check. So the client cannot
 *   pre-validate uniqueness either — it can only normalize (see `./plate`) and handle
 *   the 409.
 * - **The last remaining car cannot be deleted** — 409 `last_car_undeletable`.
 *   Registration made the first car mandatory because an order is impossible without
 *   one, so an account must never be able to return to the zero-car state.
 */
import { apiRequest } from '@/shared/api/client';

/**
 * What we fuel with — a directory, like the vehicle categories next to it.
 *
 * This app used to hold the four seeded codes as a constant, and both halves of that mistake
 * happened on the dev stand within a month: АИ-100 was added in the admin panel and never showed
 * up here, and АИ-98 was removed and stayed — a choice that would have been refused at save time
 * with `unknown fuel_type`. A list that lives in an admin panel cannot live in a bundle.
 */
export interface FuelType {
  code: string;
  name: string;
  /**
   * Принимаем ли сейчас заказы на этом топливе.
   *
   * Выключенный вид из справочника НЕ пропадает: у людей уже сохранены машины на нём, и исчезни он
   * — в парке осталась бы машина без топлива, а человек не понял бы, что случилось. Вместо этого
   * строка помечается, а заказ по такой машине не даётся.
   *
   * Необязательное — старый бэкенд поля не отдаёт, и его молчание читается как «принимаем»: это то
   * поведение, которое было до появления тумблера.
   */
  is_available?: boolean;
  /** Что показать человеку вместо кнопки заказа. Слова пишет администратор. */
  unavailable_note?: string | null;
  /**
   * Цена за литр, если она настроена. Витрина: точную сумму всё равно считает расчёт заказа.
   *
   * Пусто бывает не только «цену не завели»: цена принадлежит ГОРОДУ, и справочник, прочитанный
   * без адреса при нескольких городах, честно молчит вместо того, чтобы назвать цену случайного.
   * Тогда за ценой человек идёт на «Цены на топливо» — [[fetchFuelPrices]].
   */
  price_per_liter?: string | null;
}

/** Один город прайса: как называется, из каких пятен состоит и почём в нём литр. */
export interface FuelPriceRegion {
  administrative_level_id: string;
  name: string;
  /** Публичные названия пятен доставки — «Москва», «Химки». Может быть пусто. */
  cities: string[];
  fuels: FuelType[];
}

/** «Не принимаем» — это ЯВНОЕ `false`. Всё остальное, включая молчание старого сервера, значит
 *  «принимаем»: тумблер не должен закрывать заказы там, где о нём ещё не знают. */
export function isFuelAccepted(fuel: Pick<FuelType, 'is_available'> | undefined): boolean {
  return fuel?.is_available !== false;
}

/**
 * Live types only: the server refuses a removed one anyway, so offering it leads to a refusal.
 *
 * С координатами ответ городской: и цена, и стоп берутся у зоны, обслуживающей точку. Передавать
 * их нужно везде, где адрес уже известен, — иначе на шаге объёма будет цена «вообще», а счёт
 * придёт по цене города, и разницу человек заметит уже на оплате.
 */
export function fetchFuelTypes(at?: { lat: number; lon: number } | null): Promise<FuelType[]> {
  const query =
    at == null ? '' : `?lat=${encodeURIComponent(at.lat)}&lon=${encodeURIComponent(at.lon)}`;
  return apiRequest<FuelType[]>(`/v1/consumer/fuel-types${query}`);
}

/**
 * Прайс по городам — то, что показывает экран «Цены на топливо».
 *
 * Города приходят из зон обслуживания, поэтому список никогда не расходится с тем, где мы правда
 * работаем: появилось новое пятно — появился город; выключили — исчез.
 */
export function fetchFuelPrices(): Promise<FuelPriceRegion[]> {
  return apiRequest<{ regions: FuelPriceRegion[] }>('/v1/consumer/fuel-prices').then(
    (r) => r.regions,
  );
}

/** What most cars actually take. Not a claim that it exists — see `preferredFuelCode`. */
const COMMON_FUEL_CODE = 'AI_95';

/**
 * Which type a NEW vehicle should start on.
 *
 * «Whatever the directory lists first» is not an answer: the server sorts by code, so the moment
 * АИ-100 was added it became the default for every new car in the app — a niche high-octane picked
 * because it sorts before АИ-92. Worse than merely wrong: a выезд only takes vehicles on ONE fuel,
 * so a car silently defaulted to АИ-100 cannot join an АИ-95 trip and simply vanishes from the
 * picker behind a one-line explanation.
 *
 * So: the common one when the city has it, and only then whatever comes first.
 */
export function preferredFuelCode(types: FuelType[], fallback: string): string {
  if (types.length === 0) return fallback;
  return types.some((t) => t.code === COMMON_FUEL_CODE) ? COMMON_FUEL_CODE : (types[0]?.code ?? fallback);
}

export interface ConsumerCar {
  id: string;
  /**
   * Normalized uppercase-Latin plate — the canonical stored form, not what was typed.
   *
   * **Empty for anything that has no plate in the real world**: a boat, a generator, a tank. The
   * object is then identified by its own name plus the category, and every place that prints a
   * plate has to cope with the blank — see `vehicleLine`.
   */
  plate: string;
  brand: string;
  model: string;
  color: string;
  fuel_type_code: string;
  /** «АИ-95» — the server's own label, so this app keeps no translation of the codes. */
  fuel_type_name: string;
  /** Decimal arrives as a STRING; never parse it for display, only for comparisons. */
  tank_volume_liters: string | null;
  /** `CAR` | `MOTO` | `BOAT` | `OTHER`, or anything an admin has added since. Always present. */
  category_code: string;
  /** «Водный транспорт» — the server's own label, so this app keeps no translation of the codes. */
  category_name: string;
  created_at: string;
  updated_at: string;
}

export interface CarInput {
  plate: string;
  /**
   * Марка, модель и цвет. Пустыми уходят там, где вид техники их не спрашивает
   * (`VehicleCategory.brand_fields`) — сервер такие поля принимает и не требует.
   */
  brand: string;
  model: string;
  color: string;
  fuel_type_code: string;
  tank_volume_liters: string | null;
  /** Omitted means `CAR` server-side; sent explicitly so nothing depends on that default. */
  category_code: string;
}

/**
 * What we fuel — a directory, not a constant in this app.
 *
 * The list is not known in advance (a boat today, a snowmobile tomorrow) and adding one must not
 * cost a release. Same reason fuel types are a directory.
 */
export interface VehicleCategory {
  code: string;
  name: string;
  /** False for a boat or a generator — and then the form must not demand a plate. */
  plate_required: boolean;
  /**
   * Что форма делает с «Маркой», «Моделью» и «Цветом» у этого вида техники:
   *
   * - `REQUIRED` — автомобиль и мототехника: без них не сохранить, по ним машину и узнают;
   * - `OPTIONAL` — водный транспорт: «Yamaha 242X» осмысленно, но катер без марки — обычное дело;
   * - `NONE` — прочее (генератор, мотопомпа, цистерна): полей нет вовсе, объект описывают
   *   название и топливо.
   *
   * Неизвестное значение читается как `REQUIRED` — см. [brandFieldsOf].
   */
  brand_fields?: string;
}

/** Три состояния полей «Марка/Модель/Цвет» у вида техники. */
export type BrandFields = 'REQUIRED' | 'OPTIONAL' | 'NONE';

/**
 * Как этот вид техники относится к марке, модели и цвету.
 *
 * Незнакомое значение (и отсутствие поля — сервер постарше) читается как `REQUIRED`: так вид
 * ведёт себя по умолчанию, и так эта форма вела себя до появления признака.
 */
export function brandFieldsOf(category: VehicleCategory | undefined): BrandFields {
  if (category?.brand_fields === 'NONE') return 'NONE';
  if (category?.brand_fields === 'OPTIONAL') return 'OPTIONAL';
  return 'REQUIRED';
}

/** In the server's own display order. */
export function fetchVehicleCategories(): Promise<VehicleCategory[]> {
  return apiRequest<VehicleCategory[]>('/v1/consumer/vehicle-categories');
}

/** The one category every account has, and the one this app falls back to. */
export const DEFAULT_CATEGORY_CODE = 'CAR';

/**
 * How to name one vehicle in a list: «Toyota Camry · А123ВС77», or «Yamaha 242X · Водный транспорт»
 * when it has no plate.
 *
 * The plate is what tells two identical cars apart, so it wins where it exists. Where it does not,
 * the category is the next most useful thing — and a trailing “· ” with nothing after it is the
 * shape this function exists to prevent.
 */
export function vehicleLine(car: {
  brand: string;
  model: string;
  plate: string;
  category_name?: string;
}): string {
  const name = `${car.brand} ${car.model}`.trim();
  const tail = car.plate.trim().length > 0 ? car.plate.trim() : (car.category_name ?? '').trim();
  // У «Прочего» марку и модель не спрашивают вовсе, и склейка двух пустых строк давала строку,
  // начинающуюся с висящей точки: « · Ёмкость у ворот». Название тогда и есть всё имя.
  if (name.length === 0) return tail;
  return tail.length > 0 ? `${name} · ${tail}` : name;
}

/**
 * Как назвать технику одной строкой: «Toyota Camry», а без марки — её собственным именем
 * («Ёмкость у ворот») или хотя бы видом («Прочее»).
 *
 * Марки может не быть вовсе: у «Прочего» её не спрашивают (`VehicleCategory.brand_fields`), и
 * склейка двух пустых строк давала пустой заголовок — «Удалить ?» в листе подтверждения.
 */
export function vehicleTitle(car: {
  brand: string;
  model: string;
  plate: string;
  category_name?: string;
}): string {
  const make = `${car.brand} ${car.model}`.trim();
  if (make.length > 0) return make;
  const plate = car.plate.trim();
  return plate.length > 0 ? plate : (car.category_name ?? '').trim();
}

/**
 * Название с уточнением цвета: «Toyota Camry · синяя». Цвет у «Прочего» не спрашивают вовсе, и
 * тогда остаётся одно название — без висящей точки.
 */
export function vehicleTitleWithColor(
  car: { brand: string; model: string; plate: string; category_name?: string },
  color: string,
): string {
  const title = vehicleTitle(car);
  const tail = color.trim();
  return tail.length > 0 ? `${title} · ${tail}` : title;
}

/** Non-removed cars, oldest first — a bare array, not a paginated envelope. */
export function fetchCars(): Promise<ConsumerCar[]> {
  return apiRequest<ConsumerCar[]>('/v1/consumer/cars');
}

export function createCar(input: CarInput): Promise<ConsumerCar> {
  return apiRequest<ConsumerCar>('/v1/consumer/cars', { method: 'POST', body: input });
}

export function updateCar(id: string, input: CarInput): Promise<ConsumerCar> {
  return apiRequest<ConsumerCar>(`/v1/consumer/cars/${id}`, { method: 'PUT', body: input });
}

export function deleteCar(id: string): Promise<void> {
  return apiRequest<void>(`/v1/consumer/cars/${id}`, { method: 'DELETE' });
}

/** Server-side bound from `validate_tank_capacity_liters`: > 0 and <= 300 litres. */
export const MAX_TANK_VOLUME_LITERS = 300;

/**
 * Turns what the driver typed into what the wire expects, or reports why it cannot.
 *
 * A Russian keyboard produces a comma for the decimal separator and the backend parses
 * a Decimal, which only accepts a dot — so the comma is translated rather than rejected.
 * An empty field is a valid answer: tank volume is the one optional field on the form.
 */
export function parseTankVolume(raw: string): { value: string | null } | { error: string } {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed.length === 0) return { value: null };
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return { error: 'Объём указывается числом, например 55' };

  const asNumber = Number(trimmed);
  if (asNumber <= 0) return { error: 'Объём должен быть больше нуля' };
  if (asNumber > MAX_TANK_VOLUME_LITERS) {
    return { error: `Объём не может превышать ${MAX_TANK_VOLUME_LITERS} л` };
  }
  return { value: trimmed };
}
